import { useEffect, useRef, useCallback, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, X, Check, Crosshair, Calendar, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useActiveEvent } from '../../contexts/EventContext';
import {
  formatEventLocationLabel,
  getEventMapCenter,
  isValidGeoLocation,
} from '../../lib/eventLocation';
import type { GeoLocation } from '../../types';
import type { EventMapDrawing } from '../../lib/mapDrawings';
import MapOverlayControls from './MapOverlayControls';
import {
  type EventMapOverlay,
  type EventMapOverlaySavePayload,
  type RotatedOverlayLayer,
  createOverlayLayer,
  updateOverlayLayer,
} from '../../lib/mapOverlay';
import { useEventMapOverlay } from '../../hooks/useEventMapOverlay';
import { useEventMapDrawings } from '../../hooks/useEventMapDrawings';
import { useAppSelector } from '../../store';

interface MapLocationPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (lat: number, lng: number, address: string) => void;
  /** Current form location — used when reopening the picker after a prior selection. */
  initialLocation?: GeoLocation | null;
}

interface SerializedDrawing extends EventMapDrawing {}

const DEFAULT_CENTER: [number, number] = [51.505, -0.09];

function pickMarkerIcon() {
  return L.divIcon({
    className: 'custom-pick-marker',
    html: `<div style="width:20px;height:20px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 12px #2563eb80;"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function eventMarkerIcon() {
  return L.divIcon({
    className: 'custom-event-marker',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:#ef4444;border:3px solid #fff;box-shadow:0 0 10px rgba(239,68,68,0.55);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

export default function MapLocationPicker({
  open,
  onClose,
  onSelect,
  initialLocation,
}: MapLocationPickerProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId, activeEvent, loading: eventLoading } = useActiveEvent();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const eventMarkerRef = useRef<L.Marker | null>(null);
  const drawingsLayerRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const overlayLayerRef = useRef<RotatedOverlayLayer | null>(null);
  const [pickedLatLng, setPickedLatLng] = useState<[number, number] | null>(null);
  const [address, setAddress] = useState('');
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const overlaySaveTimerRef = useRef<number | null>(null);
  const {
    overlay: mapOverlay,
    saving: overlaySaving,
    saveOverlay,
    removeOverlay,
    setOverlayLocal,
  } = useEventMapOverlay({ eventId: activeEventId, token, live: open });
  const { drawings: serverDrawings } = useEventMapDrawings({
    eventId: activeEventId,
    token,
    live: open,
    migrateLocal: false,
  });

  const eventCenter = getEventMapCenter(activeEvent);
  const defaultPick = isValidGeoLocation(initialLocation)
    ? {
        lat: initialLocation.latitude,
        lng: initialLocation.longitude,
        address:
          initialLocation.address?.trim() ||
          `${initialLocation.latitude.toFixed(6)}, ${initialLocation.longitude.toFixed(6)}`,
      }
    : eventCenter
      ? {
          lat: eventCenter[0],
          lng: eventCenter[1],
          address: formatEventLocationLabel(activeEvent),
        }
      : null;

  const mapCenter: [number, number] =
    defaultPick != null ? [defaultPick.lat, defaultPick.lng] : eventCenter || DEFAULT_CENTER;

  const formatAddress = (data: any, lat: number, lng: number): string => {
    if (!data || !data.address) {
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
    const a = data.address;
    const houseNumber = a.house_number || '';
    const street = a.road || a.pedestrian || a.footway || a.path || '';
    const borough =
      a.suburb ||
      a.neighbourhood ||
      a.city_district ||
      a.town ||
      a.village ||
      a.hamlet ||
      '';
    let county = '';
    const region = a.state_district || a.county || a.state || '';
    if (/london/i.test(region) || /london/i.test(a.city || '')) {
      county = 'London';
    } else {
      county = region;
    }
    const postcode = a.postcode || '';
    const streetLine = [houseNumber, street].filter(Boolean).join(' ');
    const parts = [streetLine, borough, county, postcode].filter((p) => p && p.trim());
    return parts.join(', ') || data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const reverseGeocode = (lat: number, lng: number, prefix?: string) => {
    setReverseGeocoding(true);
    fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    )
      .then((res) => res.json())
      .then((data) => {
        const formatted = formatAddress(data, lat, lng);
        setAddress(prefix ? `${prefix}, ${formatted}` : formatted);
      })
      .catch(() => {
        setAddress(prefix ? `${prefix}, ${lat.toFixed(6)}, ${lng.toFixed(6)}` : `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      })
      .finally(() => setReverseGeocoding(false));
  };

  const placePickMarker = (map: L.Map, lat: number, lng: number) => {
    const latlng = L.latLng(lat, lng);
    if (markerRef.current) {
      markerRef.current.setLatLng(latlng);
    } else {
      markerRef.current = L.marker(latlng, { icon: pickMarkerIcon() }).addTo(map);
    }
  };

  const applyPick = (map: L.Map, lat: number, lng: number, addr: string, geocode = false) => {
    setPickedLatLng([lat, lng]);
    placePickMarker(map, lat, lng);
    if (geocode) {
      reverseGeocode(lat, lng);
    } else {
      setAddress(addr);
    }
  };

  const handleOverlayChange = useCallback(
    (next: EventMapOverlaySavePayload | null) => {
      if (!next) {
        void removeOverlay();
        return;
      }

      const center = eventCenter || mapCenter;
      const payload: EventMapOverlaySavePayload = {
        ...next,
        centerLat: next.centerLat || center[0],
        centerLng: next.centerLng || center[1],
      };

      if (mapOverlay?.imageUrl) {
        setOverlayLocal({ ...payload, imageUrl: mapOverlay.imageUrl });
      }

      if (payload.imageDataUrl) {
        void saveOverlay(payload);
        return;
      }

      if (overlaySaveTimerRef.current) window.clearTimeout(overlaySaveTimerRef.current);
      overlaySaveTimerRef.current = window.setTimeout(() => {
        void saveOverlay(payload);
      }, 450);
    },
    [eventCenter, mapCenter, mapOverlay?.imageUrl, removeOverlay, saveOverlay, setOverlayLocal]
  );

  // Seed selection when the picker opens
  useEffect(() => {
    if (!open) {
      setPickedLatLng(null);
      setAddress('');
      setReverseGeocoding(false);
      setShowOverlayPanel(false);
      return;
    }
    if (defaultPick) {
      setPickedLatLng([defaultPick.lat, defaultPick.lng]);
      setAddress(defaultPick.address);
    }
  }, [open, activeEventId, defaultPick?.lat, defaultPick?.lng, defaultPick?.address]);

  // Sync overlay layer when config changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !open || !mapReady) return;

    if (!mapOverlay) {
      if (overlayLayerRef.current) {
        map.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
      }
      return;
    }

    const config = {
      ...mapOverlay,
      centerLat: mapOverlay.centerLat || mapCenter[0],
      centerLng: mapOverlay.centerLng || mapCenter[1],
    };

    if (overlayLayerRef.current) {
      updateOverlayLayer(overlayLayerRef.current, config);
    } else {
      overlayLayerRef.current = createOverlayLayer(map, config);
    }

    const onMapChange = () => overlayLayerRef.current?.bringToFront();
    map.on('moveend zoomend resize', onMapChange);
    return () => {
      map.off('moveend zoomend resize', onMapChange);
    };
  }, [mapOverlay, open, mapCenter, mapReady]);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    if (mapRef.current) return;
    if (activeEventId && eventLoading) return;

    const map = L.map(containerRef.current, {
      center: mapCenter,
      zoom: defaultPick || eventCenter ? 15 : 13,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    drawingsLayerRef.current.addTo(map);

    if (eventCenter) {
      eventMarkerRef.current = L.marker(eventCenter, { icon: eventMarkerIcon() })
        .bindTooltip('Event location', { direction: 'top', offset: [0, -8] })
        .addTo(map);
    }

    if (defaultPick) {
      placePickMarker(map, defaultPick.lat, defaultPick.lng);
    }

    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      applyPick(map, lat, lng, '', true);
    });

    mapRef.current = map;
    setMapReady(true);
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      setMapReady(false);
      overlayLayerRef.current = null;
      drawingsLayerRef.current.clearLayers();
      eventMarkerRef.current = null;
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [open, activeEventId, eventLoading]);

  // Render server-synced drawings on the picker map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    drawingsLayerRef.current.clearLayers();

    serverDrawings.forEach((drawing) => {
      let layer: L.Layer | null = null;

      try {
        if (drawing.type === 'marker' && drawing.latlng) {
          layer = L.marker([drawing.latlng.lat, drawing.latlng.lng]);
        } else if (drawing.type === 'circle' && drawing.latlng && drawing.radius) {
          layer = L.circle([drawing.latlng.lat, drawing.latlng.lng], {
            radius: drawing.radius,
            color: drawing.color || '#2563eb',
            fillColor: drawing.color || '#2563eb',
            fillOpacity: 0.2,
          });
        } else if (drawing.type === 'polyline' && drawing.latlngs && Array.isArray(drawing.latlngs)) {
          const latlngs = drawing.latlngs
            .filter((ll) => ll && typeof ll.lat === 'number' && typeof ll.lng === 'number')
            .map((ll) => [ll.lat, ll.lng] as [number, number]);
          if (latlngs.length > 1) {
            layer = L.polyline(latlngs, { color: drawing.color || '#f97316' });
          }
        } else if (drawing.type === 'polygon' && drawing.latlngs && Array.isArray(drawing.latlngs)) {
          const ring = Array.isArray(drawing.latlngs[0]) ? drawing.latlngs[0] : drawing.latlngs;
          const latlngs = ring
            .filter((ll: any) => ll && typeof ll.lat === 'number' && typeof ll.lng === 'number')
            .map((ll: any) => [ll.lat, ll.lng] as [number, number]);
          if (latlngs.length > 2) {
            layer = L.polygon([latlngs], {
              color: drawing.color || '#7c3aed',
              fillColor: drawing.color || '#7c3aed',
              fillOpacity: 0.2,
            });
          }
        }

        if (!layer) return;

        const popupContent = `
          <div style="padding: 4px;">
            <div style="font-weight: bold; font-size: 11px; margin-bottom: 2px;">${drawing.label || drawing.type}</div>
            ${drawing.notes ? `<div style="font-size: 10px; color: #666;">${drawing.notes}</div>` : ''}
            <div style="font-size: 9px; color: #999; margin-top: 4px; font-style: italic;">Click to use as incident location</div>
          </div>
        `;
        layer.bindPopup(popupContent, { maxWidth: 200 });

        layer.on('click', (e: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(e);

          let locationLatLng: L.LatLng;
          if (drawing.type === 'marker') {
            locationLatLng = (layer as L.Marker).getLatLng();
          } else if (drawing.type === 'circle') {
            locationLatLng = (layer as L.Circle).getLatLng();
          } else if (drawing.type === 'polygon') {
            locationLatLng = (layer as L.Polygon).getBounds().getCenter();
          } else if (drawing.type === 'polyline') {
            locationLatLng = (layer as L.Polyline).getBounds().getCenter();
          } else {
            return;
          }

          const { lat, lng } = locationLatLng;
          const typeLabel: Record<string, string> = {
            marker: 'MARKER',
            circle: 'CIRCLE',
            polygon: 'ZONE',
            polyline: 'ROUTE',
          };
          const prefix = drawing.label
            ? `${typeLabel[drawing.type] || ''} ${drawing.label}`.trim().toUpperCase()
            : (typeLabel[drawing.type] || drawing.type.toUpperCase());

          setPickedLatLng([lat, lng]);
          placePickMarker(map, lat, lng);
          setReverseGeocoding(true);
          fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
          )
            .then((res) => res.json())
            .then((data) => {
              setAddress(`${prefix}, ${formatAddress(data, lat, lng)}`);
            })
            .catch(() => {
              setAddress(`${prefix}, ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            })
            .finally(() => setReverseGeocoding(false));
        });

        drawingsLayerRef.current.addLayer(layer);
      } catch (error) {
        console.error('Error adding drawing to map:', error);
      }
    });
  }, [serverDrawings, mapReady]);

  const handleConfirm = () => {
    if (pickedLatLng && address) {
      onSelect(pickedLatLng[0], pickedLatLng[1], address);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={{ height: '70vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Crosshair className="w-4 h-4 text-primary" />
            Select Location on Map
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {activeEvent && (
          <div className="px-4 py-3 bg-primary/5 border-b border-border flex-shrink-0 space-y-1.5">
            <p className="text-xs font-semibold text-foreground">{activeEvent.name}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {activeEvent.eventDate}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {activeEvent.eventStartTime}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-red-500" />
                {formatEventLocationLabel(activeEvent)}
              </span>
            </div>
            {activeEvent.description && (
              <p className="text-[10px] text-muted-foreground line-clamp-2">{activeEvent.description}</p>
            )}
          </div>
        )}

        <div className="px-4 py-2 border-b border-border flex-shrink-0 space-y-2">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-primary" />
            {defaultPick
              ? 'Incident location defaults to the event site — click elsewhere to adjust, or confirm below.'
              : 'Click on the map to set the incident location. The address will be auto-detected.'}
          </p>
          <button
            type="button"
            onClick={() => setShowOverlayPanel((v) => !v)}
            className={cn(
              'text-[10px] font-semibold px-2 py-1 rounded transition-colors',
              showOverlayPanel || mapOverlay
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent'
            )}
          >
            {mapOverlay ? 'Site map overlay active' : 'Site map overlay'} —{' '}
            {showOverlayPanel ? 'hide' : 'show'} controls
          </button>
          {showOverlayPanel && (
            <MapOverlayControls
              overlay={mapOverlay}
              defaultCenter={mapCenter}
              onChange={handleOverlayChange}
              saving={overlaySaving}
            />
          )}
        </div>

        <div ref={containerRef} className="flex-1 min-h-0" />

        <div className="px-4 py-3 border-t border-border flex-shrink-0 bg-card">
          {pickedLatLng ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground font-mono mb-0.5">
                  {pickedLatLng[0].toFixed(6)}, {pickedLatLng[1].toFixed(6)}
                </p>
                <p
                  className={cn(
                    'text-xs text-foreground truncate',
                    reverseGeocoding && 'text-muted-foreground italic'
                  )}
                >
                  {reverseGeocoding ? 'Detecting address...' : address}
                </p>
              </div>
              <button
                onClick={handleConfirm}
                disabled={reverseGeocoding || !address}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                Confirm Location
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center">
              Click anywhere on the map to select a location
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
